/* eslint-disable spire/export-styles */
import ToasterContext from "@aspen/MobiusOverrides/Toast/ToasterContext";
import AspenProductPrice from "@aspen/Store/Aspen/Components/AspenProductPrice";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, ProductContextModel, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    className?: string;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        checkoutShipping: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        session: getSession(state),
        userName: state.context.session.userName,
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isUpdatingCart: state.context.isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

function getParentByClassName(element: any, className: string) {
    let parent = element.parentNode;
    while (parent !== null) {
        if (parent.classList && parent.classList.contains(className)) {
            return parent;
        }
        parent = parent.parentNode;
    }
    return null;
}

const ProductAddToCartButton = ({
    productContext,
    hasEnoughInventory,
    isUpdatingCart,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    className,
    cartUrl,
    checkoutShipping,
    session,
    isAddToCartButtonDisabled,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    if ((!hasEnoughInventory && !productContext.productInfo.failedToLoadInventory) || !canAddToCart) {
        return (<Button
            {...styles}
            disabled={true}
            className={className} style={{ visibility: "hidden" }}
        >
            {labelOverride ?? translate("Add to Cart")}
        </Button>);
    }
    const getAddToCartPopupBody = (productContext: ProductContextModel) => {
        return <>
            <div className="as-card-holder">
                <strong className="as-ttl">{translate("Added to Cart")}</strong>
                <div className="as-product-holder">
                    <a href={productContext.productInfo.productDetailPath} className="as-image">
                        <img src={productContext.product.largeImagePath || productContext.product.mediumImagePath || productContext.product.smallImagePath} alt="description" />
                    </a>
                    <div className="as-text-area">
                        <a className="as-subtitle" href={productContext.product.brand?.detailPagePath}>{productContext.product.brand?.name}</a>
                        <a href={productContext.productInfo.productDetailPath} className="as-product-ttl">{productContext.product.properties?.ifsErpDescription || productContext.product.productTitle}</a>
                        <span className="as-text">{productContext.product.productNumber}</span>
                        {session.isAuthenticated && <span className="as-text"><AspenProductPrice product={productContext} showLabel={false} showUnitOfMeasure={false}></AspenProductPrice></span>}
                        <span className="as-text">{translate("Qty:")} {productContext.productInfo.qtyOrdered === 0 ? 1 : productContext.productInfo.qtyOrdered}</span>
                    </div>
                </div>
                {!session.isAuthenticated &&  <div className="as-product-holder add-to-cart-notes">{siteMessage("AddToCart_Notes")}</div>}
                <div className="as-wrap-btn">
                <Button onClick={() => cartUrl && (window.location.href = cartUrl)} className="as-btn as-btn-outline button-primary-cta">{translate("View Cart")}</Button>
                    {session.isAuthenticated && <Button onClick={() => checkoutShipping && (window.location.href = checkoutShipping)} className="as-btn as-btn-primary button-primary-cta">{translate("Checkout")}</Button>}
                </div>
            </div>
        </>;
    }
    const addToCartClickHandler = async (event: any) => {
        event.preventDefault();
        if (!productContext.product.quoteRequired && !productContext.product.allowZeroPricing && productContext.productInfo.pricing && getUnitNetPrice(productContext.productInfo.pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger", timeoutLength: addToCartPopupTimeout });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                    .filter(sectionOptionId => !!sectionOptionId)
                    .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        const cartline = (await addToCart({
            productId: productContext.product.id,
            qtyOrdered: productContext.productInfo.qtyOrdered === 0 ? 1 : productContext.productInfo.qtyOrdered,
            unitOfMeasure: productContext.productInfo.unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: getAddToCartPopupBody(productContext),
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout, isAddToCartConfirmation: true
            });
        }
    };
    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            {...otherProps} className={className} disabled={(productContext.productInfo.qtyOrdered <= 0 || isAddToCartButtonDisabled)}
        >
            {labelOverride ?? translate("Add to Cart")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
